const { logWarn, logError, logDebug } = require('./lib/logger');
const { loadScript, loadScriptWithin } = require('./lib/scriptLoader');
const { ensurePrebidFallback, PREBID_LOAD_FAILSAFE_MS } = require('./lib/prebidFallback');
const { configurePrebid } = require('./lib/prebidConfig');
const { setupAutoRefresh } = require('./lib/autoRefresh');
const {
  applyContentAdSticky,
  clearContentAdSticky
} = require('./lib/contentAdSticky');
const { createPageAdRuntime } = require('./lib/adRuntime');
const { applySlotSizeFromPrebid, ensureAdSlotMinHeight } = require('./lib/adSizeUtils');
const { onElementInViewport } = require('./lib/viewportObserver');
const { resolveCoordinatedRequestThreshold } = require('./lib/adViewability');
const { ensureProLinkStyles, buildProUpgradeUrl } = require('./lib/proLinks');
const { createNewsletterIframeModule } = require('./lib/newsletter');
const { createContestIframeModule } = require('./lib/contest');
const {
  createInsightsIframeModule,
  createPredictionIframeModule,
  ensureInsightsCompletionListener,
  extendArticleEndSlotDecision,
  fetchActiveEmbedItem,
  isInsightsFrequencyCapped,
  isPredictionFrequencyCapped,
  isSponsoredContent,
  listCompletedInsightsSurveyIds,
  readArticleTags,
  resolveInsightsArticleId,
  settleArticleEndSlot,
  shouldSuppressInsightSurveyEmbed,
  waitForArticleEndSlot,
  waitForStudyGroup
} = require('./lib/insights');
const { STUDY_GROUP_WAIT_MS } = require('./insightsStudyConfig');
const { setupMemberArticleAds } = require('./lib/memberAds');
const { injectStyles } = require('./lib/utils/styleInjector');
const { buildCdmPageOptions } = require('./lib/cdmAdtest');
const {
  createArticleInfiniteAdInserter,
  setupArticleInfiniteScrollAdsObserver
} = require('./lib/articleInfiniteAds');
const {
  buildArticleGamTargeting,
  applyGamTargeting
} = require('./lib/gamTargeting');
const {
  applyPrebidTargetingToSlots,
  applySlotTargeting,
  configurePageSettings,
  disableSlotCollapse,
  enableLazyLoad
} = require('./lib/gptConfig');
const {
  hideTopAdWrapper,
  injectTopAndSkinAdContainers,
  isSkinAdEligible,
  prepareSkinAdSlot,
  shouldHideTopAdWrapper,
  setupTabletStickyAd
} = require('./lib/desktopArticle');
const {
  createDesktopAnchorAd,
  isDesktopAnchorEligible
} = require('./lib/desktopAnchorAd');
const { resolveDesktopSidebarPlacement } = require('./lib/sidebarPlacement');
const { createStoriesPlayer } = require('./lib/storiesPlayer');
const { initShowAwareArticle } = require('./lib/storiesPlayer/articlePlacement');
const { storiesAdOptions } = require('./lib/storiesPlayer/adOptions');

const {
  resolveLanguage,
  getProLinkText,
  getDefaultAdvertisementLabel,
  buildRelatedSearchContainerHtml,
  getSlotPath,
  emitAdEvent
} = require('./adsBrandUtils');

// Ad unit factory for generating Prebid ad units
const {
  makeTopAdUnit,
  makeSkinAdUnit,
  makeDtContentAdUnit,
  makeSidebarAdUnit,
  ensurePrebidAdUnitForCode
} = require('./adUnitFactory');

const INSTAGRAM_PLACEHOLDER_STYLES = `
.rm-embed-container.rm-embed-instagram {
  min-height: 420px;
}
@media (min-width: 768px) {
  .rm-embed-container.rm-embed-instagram {
    min-height: 520px;
  }
}
`;

function ensureInstagramEmbedPlaceholder() {
  try {
    injectStyles('milla-instagram-placeholder', INSTAGRAM_PLACEHOLDER_STYLES);
  } catch (_) {
    // ignore style injection failures
  }
}

function isBlockedEmbedParagraph(paragraph) {
  if (!paragraph || typeof paragraph.closest !== 'function') return false;
  return Boolean(
    paragraph.closest(
      '.rm-embed-container, .rm-shortcode, .rm-embed-holder, .rm-embed-spacer, iframe, .instagram-media'
    )
  );
}

function createArticleDesktopAds(config, options = {}) {
  const targeting = options.targeting || {};
  const resultsPageBaseUrl = options.resultsPageBaseUrl || 'http://www.narcity.com/search/?afs&partner_param=param';
  const storiesPlayerConfig = options.storiesPlayer || {};
  const device = options.device || 'desktop';
  const isTablet = device === 'tablet';

  return function initArticleDesktopAds(ctx) {
    ensureInstagramEmbedPlaceholder();
    const {
      prebidUrl,
      gptUrl,
      _noads,
      _urlonly,
      _sponsored,
      _fr_lang,
      staticPage,
      fwjson,
      userIds,
      _googCsa,
      pbjs,
      googletag,
      pro,
      emitter,
      insights,
      auctionStrategy,
      adDiagnosticsEnabled,
      pageType,
      pageKey,
      walled
    } = ctx;

    const brand = config.brand;
    const language = resolveLanguage(config, _fr_lang);
    const proLinkText = getProLinkText(language);
    const proLinkUrl = buildProUpgradeUrl(config.proLinkSource.desktop);
    const defaultAdvertisementLabel = getDefaultAdvertisementLabel(language);
    const storiesPlaylist = storiesPlayerConfig.playlist
      || (config.brand === 'mtlblog'
        ? 'mtlblog'
        : (language === 'fr' ? 'narcity_fr' : 'narcity_en'));
    const storiesPageTargeting = buildArticleGamTargeting({ fwjson, targeting, language });
    ensureProLinkStyles();
    const PREBID_TIMEOUT = 1500;
    const FAILSAFE_TIMEOUT = 3000;
    const storiesPlayer = createStoriesPlayer({
      showAware: storiesPlayerConfig.showAware === true,
      contentEnabled: storiesPlayerConfig.contentEnabled !== false,
      ...storiesAdOptions(config, ctx, storiesPlayerConfig, targeting, language),
      language,
      playlist: storiesPlaylist,
      manifestUrl: storiesPlayerConfig.manifestUrl,
      analyticsEndpoint: storiesPlayerConfig.analyticsEndpoint,
      containerSelector: '.stories-player-container',
      proLinkUrl,
      proLinkText,
      brand
    });
    initShowAwareArticle(storiesPlayer, storiesPlayerConfig, ctx, device);

    // Allow standard leaderboard plus larger takeover-style creatives.
    const topAdSizes = [
      [728, 90],
      [970, 250],
      [2000, 250]
    ];

    const dtContentSizes = [
      [300, 250],
      [336, 280],
      [320, 480],
      [1, 1]
    ];

    const skinSize = [[2000, 1200]];

    const sidebarSizes = [
      [300, 600],
      [300, 250]
    ];

    const CONTENT_SLOT_MIN_HEIGHT = 280;
    const SIDEBAR_SLOT_MIN_HEIGHT = 250;
    // Desktop CSA related searches render a 222px (4 terms) or 307px (5 terms)
    // iframe in the article column; reserving the taller one avoids a shift.
    const RELATED_SEARCH_MIN_HEIGHT = 307;
    const CONTENT_AD_PREFETCH_THRESHOLD = 3500;
    const SIDEBAR_AD_PREFETCH_THRESHOLD = 800;
    const MANAGED_AD_RENDER_FAILSAFE_MS = 4500;
    const INFINITE_SCROLL_AD_MIN_DISTANCE_PX = 2600;
    const INFINITE_SCROLL_AD_ITEM_INTERVAL = 4;
    const desktopAnchorConfig = config.articlePlacements?.desktopAnchor || {};
    const desktopSidebarPlacement = resolveDesktopSidebarPlacement(config);
    const activeSidebarSlotCount = _sponsored === 'yes'
      ? 1
      : desktopSidebarPlacement.slotCount;
    const desktopAnchorSlotId = typeof desktopAnchorConfig.slotId === 'string'
      && desktopAnchorConfig.slotId.trim()
      ? desktopAnchorConfig.slotId.trim()
      : `desktop-anchor-${brand}`;
    const desktopAnchorSizes = Array.isArray(desktopAnchorConfig.sizes)
      && desktopAnchorConfig.sizes.length > 0
      ? desktopAnchorConfig.sizes
      : [[728, 90]];
    // The anchor would cover the wall's sign-in panel (owner, 2026-10-29).
    const desktopAnchorEnabled = !walled && isDesktopAnchorEligible({
      config: desktopAnchorConfig,
      isTablet,
      isSponsored: _sponsored === 'yes',
      viewportWidth: typeof window !== 'undefined'
        ? (window.innerWidth || document.documentElement.clientWidth || 0)
        : 0
    });
    const skinEnabled = !isTablet && isSkinAdEligible(
      config,
      typeof window !== 'undefined'
        ? (window.innerWidth || document.documentElement.clientWidth || 0)
        : 0
    );
    let desktopAnchorView = null;
    let desktopAnchorGptSlot = null;
    let desktopAnchorAutoRefreshController = null;
    let desktopAnchorCoordinator = null;
    let stickySidebarResizeObserver = null;

    function ensureDesktopAnchorView() {
      if (!desktopAnchorEnabled || typeof document === 'undefined') return null;
      if (desktopAnchorView && !desktopAnchorView.isClosed()) return desktopAnchorView;
      desktopAnchorView = createDesktopAnchorAd({
        runtimeDocument: document,
        slotId: desktopAnchorSlotId,
        advertisementLabel: defaultAdvertisementLabel,
        closeLabel: language === 'fr' ? 'Fermer la publicit\u00e9' : 'Close advertisement',
        onClose: closeDesktopAnchorAd
      });
      return desktopAnchorView;
    }

    function closeDesktopAnchorAd() {
      const view = desktopAnchorView;
      if (
        desktopAnchorAutoRefreshController
        && typeof desktopAnchorAutoRefreshController.removeSlot === 'function'
      ) {
        desktopAnchorAutoRefreshController.removeSlot(desktopAnchorSlotId);
      }
      if (!desktopAnchorCoordinator && pbjs && typeof pbjs.removeAdUnit === 'function') {
        try {
          pbjs.removeAdUnit(desktopAnchorSlotId);
        } catch (_) {
          // Prebid cleanup is best-effort after a user closes the anchor.
        }
      }
      if (
        desktopAnchorGptSlot
        && googletag
        && typeof googletag.destroySlots === 'function'
      ) {
        try {
          googletag.destroySlots([desktopAnchorGptSlot]);
        } catch (_) {
          // GPT cleanup is best-effort after a user closes the anchor.
        }
      }
      desktopAnchorGptSlot = null;
      desktopAnchorView = null;
      if (view && typeof view.destroy === 'function') {
        view.destroy();
      }
    }

    // Desktop no longer renders debug placeholders; keep function for API compatibility.
    function renderDebugPlaceholders() {}

    function isContentSlotId(slotId) {
      return typeof slotId === 'string' && slotId.indexOf('dt-content-ad-') === 0;
    }

    function isSidebarSlotId(slotId) {
      return typeof slotId === 'string' && slotId.indexOf('sidebar-ad-') === 0;
    }

    function isTopSlotId(slotId) {
      return slotId === 'top-ad';
    }

    function isSkinSlotId(slotId) {
      return slotId === 'skin-ad';
    }

    function isDesktopAnchorSlotId(slotId) {
      return desktopAnchorEnabled && slotId === desktopAnchorSlotId;
    }

    function isManagedSlotId(slotId) {
      return (
        isContentSlotId(slotId) ||
        isSidebarSlotId(slotId) ||
        isTopSlotId(slotId) ||
        isSkinSlotId(slotId) ||
        isDesktopAnchorSlotId(slotId)
      );
    }

    function isTinyCreativeSize(size) {
      if (!size || !Array.isArray(size) || size.length < 2) return false;
      const width = Number(size[0]);
      const height = Number(size[1]);
      return Number.isFinite(width) && Number.isFinite(height) && width <= 1 && height <= 1;
    }

    function isCollapsibleTinyCreative(slotId, size) {
      return isTinyCreativeSize(size) && !hasPrebidWinningBid(slotId);
    }

    // Visibility, not display: the link keeps its line so the ad box doesn't
    // move by its height each time a slot goes pending and fills.
    function setAdProLinksVisible(wrapper, visible) {
      if (!wrapper || typeof wrapper.querySelectorAll !== 'function') return;
      const links = wrapper.querySelectorAll('.requires-pro');
      links.forEach((link) => {
        if (link && link.style) {
          link.style.visibility = visible ? '' : 'hidden';
        }
      });
    }

    function isOnScreen(node) {
      if (!node || typeof node.getBoundingClientRect !== 'function' || typeof window === 'undefined') {
        return false;
      }
      const rect = node.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0;
      return rect.height > 0 && rect.bottom > 0 && rect.top < viewportHeight;
    }

    function cancelOffscreenAction(slot) {
      if (!slot || !slot._millaOffscreenObserver) return;
      slot._millaOffscreenObserver.disconnect();
      slot._millaOffscreenObserver = null;
    }

    // Runs `action` now, or once `node` has left the viewport. Shrinking a box
    // the reader can see moves the article under them (a layout shift); off
    // screen, nothing visible moves (Chrome's scroll anchoring covers boxes
    // above the viewport).
    function runWhenOffscreen(slot, node, action) {
      cancelOffscreenAction(slot);
      if (!isOnScreen(node) || typeof IntersectionObserver === 'undefined') {
        action();
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) return;
        cancelOffscreenAction(slot);
        action();
      });
      slot._millaOffscreenObserver = observer;
      observer.observe(node);
    }

    function reservedContentHeight(slot) {
      const lockedHeight = Number(slot && slot.dataset && slot.dataset.millaMaxHeight);
      return Math.max(CONTENT_SLOT_MIN_HEIGHT, Number.isFinite(lockedHeight) ? lockedHeight : 0);
    }

    function resetBoxStyles(node) {
      if (!node || !node.style) return;
      node.style.display = '';
      node.style.height = '';
      node.style.minHeight = '';
      node.style.width = '';
      node.style.minWidth = '';
      node.style.margin = '';
      node.style.padding = '';
      node.style.overflow = '';
      node.style.visibility = '';
      if (typeof node.removeAttribute === 'function') {
        node.removeAttribute('aria-hidden');
      }
    }

    function zeroBoxStyles(node, hide) {
      if (!node || !node.style) return;
      if (hide) {
        node.style.display = 'none';
      }
      node.style.height = '0px';
      node.style.minHeight = '0px';
      node.style.width = hide ? '0px' : '';
      node.style.minWidth = '0px';
      node.style.margin = '0';
      node.style.padding = '0';
      node.style.overflow = 'hidden';
      if (hide && typeof node.setAttribute === 'function') {
        node.setAttribute('aria-hidden', 'true');
      }
    }

    function getAdWrapper(slot) {
      if (!slot || typeof slot.closest !== 'function') return null;
      return slot.closest('.ad-wrapper');
    }

    function getAdFlex(slot) {
      if (!slot || typeof slot.closest !== 'function') return null;
      return slot.closest('.ad-flex');
    }

    function getSidebarItem(slot) {
      if (!slot || typeof slot.closest !== 'function') return null;
      return slot.closest('.sidebar-item');
    }

    function getSidebarContainer(slot) {
      if (!slot || typeof slot.closest !== 'function') return null;
      return slot.closest('.sidebar-ad-container');
    }

    function getTopAdWrapper(slot) {
      if (!slot || typeof slot.closest !== 'function') return null;
      return slot.closest('.top-ad-wrapper');
    }

    function getTopAdFlex(slot) {
      if (!slot || typeof slot.closest !== 'function') return null;
      return slot.closest('.top-ad-flex');
    }

    function clearManagedSlotFailsafe(slot) {
      if (!slot || !slot._millaRenderFailsafeTimer) return;
      clearTimeout(slot._millaRenderFailsafeTimer);
      slot._millaRenderFailsafeTimer = null;
    }

    function markManagedSlotState(slot, state) {
      if (!slot || !slot.dataset) return;
      slot.dataset.millaAdState = state;
    }

    function hasRenderedAdChild(slot) {
      if (!slot || !slot.childNodes) return false;
      return Array.prototype.some.call(slot.childNodes, (node) => {
        if (!node || node.nodeType !== 1) return false;
        return !node.classList || !node.classList.contains('milla-ad-placeholder');
      });
    }

    function collapseDecorativeSibling(node) {
      if (!node || !node.classList) return;
      if (
        node.classList.contains('separator') ||
        (
          node.classList.contains('related-post') &&
          !(node.textContent || '').trim() &&
          (!node.children || node.children.length === 0)
        )
      ) {
        zeroBoxStyles(node, true);
      }
    }

    function shouldCollapseSidebarContainer(container) {
      if (!container || !container.children) return false;
      return !Array.prototype.some.call(container.children, (child) => {
        if (!child || !child.classList) return false;
        if (child.classList.contains('sidebar-item')) return false;
        return child.style ? child.style.display !== 'none' : true;
      });
    }

    function prepareManagedAdSlot(slot) {
      if (!slot || !isManagedSlotId(slot.id)) return;
      markManagedSlotState(slot, 'pending');
      if (isContentSlotId(slot.id)) {
        const wrapper = getAdWrapper(slot);
        if (wrapper && wrapper.dataset) {
          wrapper.dataset.millaAdState = 'pending';
        }
        setAdProLinksVisible(wrapper, false);
      }
    }

    function releaseManagedAdReservation(slot) {
      if (!slot || !isManagedSlotId(slot.id)) return;
      // A content slot keeps its reservation until GPT renders: releasing it on
      // a missing Prebid bid collapses the article, then GAM re-expands it.
      if (isContentSlotId(slot.id)) return;
      zeroBoxStyles(slot, false);

      if (isSidebarSlotId(slot.id)) {
        zeroBoxStyles(getSidebarItem(slot), false);
      }
    }

    function restoreManagedAdSlot(slot) {
      if (!slot || !isManagedSlotId(slot.id)) return;
      clearManagedSlotFailsafe(slot);
      resetBoxStyles(slot);
      markManagedSlotState(slot, 'filled');

      if (isContentSlotId(slot.id)) {
        cancelOffscreenAction(slot);
        // Keep the reservation: a shorter creative sits (sticky) inside it
        // rather than the article shrinking around it.
        ensureAdSlotMinHeight(slot, reservedContentHeight(slot));
        const flex = getAdFlex(slot);
        const wrapper = getAdWrapper(slot);
        resetBoxStyles(flex);
        resetBoxStyles(wrapper);
        if (wrapper && wrapper.dataset) {
          wrapper.dataset.millaAdState = 'filled';
        }
        setAdProLinksVisible(wrapper, true);
      } else if (isSidebarSlotId(slot.id)) {
        resetBoxStyles(getSidebarItem(slot));
        resetBoxStyles(getSidebarContainer(slot));
      } else if (isTopSlotId(slot.id)) {
        resetBoxStyles(getTopAdFlex(slot));
        resetBoxStyles(getTopAdWrapper(slot));
      } else if (isSkinSlotId(slot.id)) {
        // The skin creative draws outside its slot; the slot stays 0px tall.
        prepareSkinAdSlot(slot);
      } else if (isDesktopAnchorSlotId(slot.id) && desktopAnchorView) {
        desktopAnchorView.show();
      }
    }

    function collapseManagedAdSlot(slot) {
      if (!slot || !isManagedSlotId(slot.id)) return;
      if (typeof window !== 'undefined' && window._millaShowAdPlaceholders === true) {
        return;
      }

      if (isContentSlotId(slot.id)) {
        clearContentAdSticky(slot);
      }

      clearManagedSlotFailsafe(slot);
      markManagedSlotState(slot, 'collapsed');

      if (isContentSlotId(slot.id)) {
        const flex = getAdFlex(slot);
        const wrapper = getAdWrapper(slot);
        if (wrapper && wrapper.dataset) {
          wrapper.dataset.millaAdState = 'collapsed';
        }
        setAdProLinksVisible(wrapper, false);

        runWhenOffscreen(slot, wrapper || slot, () => {
          if (!slot.dataset || slot.dataset.millaAdState !== 'collapsed') return;
          zeroBoxStyles(slot, true);
          zeroBoxStyles(flex, true);
          zeroBoxStyles(wrapper, true);
          if (wrapper) {
            const prev = wrapper.previousElementSibling;
            collapseDecorativeSibling(prev);
            if (prev && prev.classList && prev.classList.contains('related-post')) {
              collapseDecorativeSibling(prev.previousElementSibling);
            }
            collapseDecorativeSibling(wrapper.nextElementSibling);
          }
        });
        return;
      }

      zeroBoxStyles(slot, true);
      if (isSidebarSlotId(slot.id)) {
        zeroBoxStyles(getSidebarItem(slot), true);
        const container = getSidebarContainer(slot);
        if (shouldCollapseSidebarContainer(container)) {
          zeroBoxStyles(container, true);
        }
      } else if (isDesktopAnchorSlotId(slot.id) && desktopAnchorView) {
        desktopAnchorView.collapse();
      }
      // An empty top slot leaves its wrapper alone: the template reserves the
      // wrapper's height on desktop before the SDK runs, so hiding it would pull
      // the whole page up (as homepages already do, the band stays).
    }

    function scheduleManagedSlotFailsafe(slotId) {
      if (
        typeof window === 'undefined' ||
        !isManagedSlotId(slotId) ||
        window._millaShowAdPlaceholders === true
      ) {
        return;
      }
      const slot = document.getElementById(slotId);
      if (!slot) return;
      clearManagedSlotFailsafe(slot);
      slot._millaRenderFailsafeTimer = window.setTimeout(() => {
        const currentSlot = document.getElementById(slotId);
        if (!currentSlot) return;
        const state = currentSlot.dataset && currentSlot.dataset.millaAdState;
        if (state === 'filled' || state === 'collapsed') return;
        if (hasRenderedAdChild(currentSlot)) {
          restoreManagedAdSlot(currentSlot);
          return;
        }
        collapseManagedAdSlot(currentSlot);
      }, MANAGED_AD_RENDER_FAILSAFE_MS);
    }

    function collapseAllPendingManagedSlots() {
      if (typeof document === 'undefined') return;
      const slots = document.querySelectorAll(
        `.content-ad[id^="dt-content-ad-"], .sidebar-content[id^="sidebar-ad-"], #top-ad, #skin-ad, #${desktopAnchorSlotId}`
      );
      slots.forEach((slot) => {
        if (!slot || (slot.dataset && slot.dataset.millaAdState === 'filled')) return;
        collapseManagedAdSlot(slot);
      });
    }

    function hasPrebidWinningBid(slotId) {
      if (!slotId || !pbjs || typeof pbjs.getAdserverTargetingForAdUnitCode !== 'function') {
        return false;
      }
      const targeting = pbjs.getAdserverTargetingForAdUnitCode(slotId) || {};
      return Boolean(
        targeting.hb_adid ||
        targeting.hb_bidder ||
        targeting.hb_pb ||
        targeting.hb_size
      );
    }

    function configureStickySidebarRail(adContainer, adItem, sidebar) {
      if (!adContainer || !adItem || !sidebar) return;
      adContainer.classList.add('milla-sticky-sidebar-rail');
      adContainer.style.position = 'relative';
      adItem.style.position = 'sticky';
      adItem.style.top = `${desktopSidebarPlacement.stickyTopPx}px`;

      const syncRailHeight = () => {
        const mainColumn = document.querySelector('.main-column');
        if (!mainColumn) return;
        const railOffset = Number(adContainer.offsetTop || 0);
        const availableHeight = Math.max(
          600,
          Number(mainColumn.offsetHeight || 0) - railOffset
        );
        adContainer.style.minHeight = `${availableHeight}px`;
      };

      syncRailHeight();
      if (typeof ResizeObserver !== 'undefined') {
        if (stickySidebarResizeObserver) {
          stickySidebarResizeObserver.disconnect();
        }
        stickySidebarResizeObserver = new ResizeObserver(syncRailHeight);
        const mainColumn = document.querySelector('.main-column');
        if (mainColumn) stickySidebarResizeObserver.observe(mainColumn);
      } else if (typeof window !== 'undefined') {
        window.setTimeout(syncRailHeight, 1000);
      }
    }

    function injectSidebarNewsletterAndAds() {
      if (typeof document === 'undefined') {
        return;
      }
      const sidebar = document.querySelector('.sidebar');
      if (!sidebar) {
        logWarn('Sidebar container not found; skipping sidebar injection.');
        return;
      }

      const translationsObj =
        (typeof window !== 'undefined' && window.translations) || {};
      const advertisementLabel =
        translationsObj.advertisementLabel || defaultAdvertisementLabel;

      const shouldShowNewsletter =
        _sponsored === 'yes'
          ? true
          : (typeof ctx === 'object' && ctx && ctx.isLoggedIn === true ? false : true);

      let newsletterBox = sidebar.querySelector('.milla-newsletter-embed');
      if (shouldShowNewsletter && !newsletterBox) {
        newsletterBox = createNewsletterIframeModule('sidebar');
        if (newsletterBox) {
          newsletterBox.classList.add('milla-newsletter-embed');
          sidebar.appendChild(newsletterBox);
        }
      }

      if (_sponsored === 'yes') {
        // Sponsored articles: keep only newsletter + one sticky sidebar ad (no video).
        const existingAdContainers = Array.from(
          sidebar.querySelectorAll('.sidebar-ad-container')
        );
        existingAdContainers.forEach((node) => {
          if (node && node.parentElement) {
            node.parentElement.removeChild(node);
          }
        });
        const storiesContainers = Array.from(
          sidebar.querySelectorAll('.stories-player-container')
        );
        storiesContainers.forEach((node) => {
          if (node && node.parentElement) {
            node.parentElement.removeChild(node);
          }
        });

        const slotId = 'sidebar-ad-1';
        if (!document.getElementById(slotId)) {
          const adContainer = document.createElement('div');
          adContainer.classList.add('sidebar-ad-container');
          adContainer.style.position = 'sticky';
          adContainer.style.top = '24px';

          const adItem = document.createElement('div');
          adItem.classList.add('sidebar-item');

          const adLabel = document.createElement('div');
          adLabel.classList.add('sidebar-ad-label');
          adLabel.setAttribute('data-translate-key', 'advertisementLabel');
          adLabel.textContent = advertisementLabel;

          const adContent = document.createElement('div');
          adContent.id = slotId;
          adContent.classList.add('sidebar-content');
          ensureAdSlotMinHeight(adContent, SIDEBAR_SLOT_MIN_HEIGHT);
          prepareManagedAdSlot(adContent);

          adItem.appendChild(adLabel);
          adItem.appendChild(adContent);
          adContainer.appendChild(adItem);

          if (newsletterBox && newsletterBox.parentElement) {
            newsletterBox.parentElement.insertBefore(
              adContainer,
              newsletterBox.nextSibling
            );
          } else {
            sidebar.insertBefore(adContainer, sidebar.firstChild);
          }
        }

        return;
      }

      Array.from(sidebar.querySelectorAll('.sidebar-content[id^="sidebar-ad-"]')).forEach((slot) => {
        const slotIndex = Number((slot.id.match(/-(\d+)$/) || [])[1]);
        if (!Number.isFinite(slotIndex) || slotIndex <= activeSidebarSlotCount) return;
        const container = typeof slot.closest === 'function'
          ? slot.closest('.sidebar-ad-container')
          : null;
        if (container && container.parentElement) {
          container.parentElement.removeChild(container);
        }
      });

      let existingStoriesPlayer = sidebar.querySelector('.stories-player-container');
      if (!existingStoriesPlayer) {
        const storiesWrapper = document.createElement('div');
        storiesWrapper.className = 'sidebar-ad-container';

        const storiesContainer = document.createElement('div');
        storiesContainer.className = 'stories-player-container';
        storiesContainer.style.padding = '15px';
        storiesContainer.style.background = '#F9F9F9';
        storiesContainer.style.boxSizing = 'border-box';

        const proLink = document.createElement('a');
        proLink.href = proLinkUrl;
        proLink.target = '_blank';
        proLink.rel = 'noopener noreferrer';
        proLink.className = 'requires-pro milla-pro-link';
        proLink.textContent = proLinkText;

        storiesContainer.appendChild(proLink);

        const adItem = document.createElement('div');
        adItem.className = 'sidebar-item';

        const adLabel = document.createElement('div');
        adLabel.className = 'sidebar-ad-label';
        adLabel.setAttribute('data-translate-key', 'advertisementLabel');
        adLabel.textContent = advertisementLabel;

        const adContent = document.createElement('div');
        adContent.id = 'sidebar-ad-1';
        adContent.className = 'sidebar-content';
        ensureAdSlotMinHeight(adContent, SIDEBAR_SLOT_MIN_HEIGHT);
        prepareManagedAdSlot(adContent);

        adItem.appendChild(adLabel);
        adItem.appendChild(adContent);

        storiesWrapper.appendChild(storiesContainer);
        storiesWrapper.appendChild(adItem);

        if (newsletterBox && newsletterBox.parentElement) {
          newsletterBox.parentElement.insertBefore(
            storiesWrapper,
            newsletterBox.nextSibling
          );
        } else {
          sidebar.insertBefore(storiesWrapper, sidebar.firstChild);
        }
      }

      for (let i = 2; i <= activeSidebarSlotCount; i += 1) {
        const slotId = `sidebar-ad-${i}`;
        const existingSlot = document.getElementById(slotId);
        if (existingSlot) {
          if (i === desktopSidebarPlacement.stickySlotIndex) {
            configureStickySidebarRail(
              existingSlot.closest('.sidebar-ad-container'),
              existingSlot.closest('.sidebar-item'),
              sidebar
            );
          }
          continue;
        }

        const adContainer = document.createElement('div');
        adContainer.classList.add('sidebar-ad-container');

        const adItem = document.createElement('div');
        adItem.classList.add('sidebar-item');

        const adLabel = document.createElement('div');
        adLabel.classList.add('sidebar-ad-label');
        adLabel.setAttribute('data-translate-key', 'advertisementLabel');
        adLabel.textContent = advertisementLabel;

        const adContent = document.createElement('div');
        adContent.id = slotId;
        adContent.classList.add('sidebar-content');
        ensureAdSlotMinHeight(adContent, SIDEBAR_SLOT_MIN_HEIGHT);
        prepareManagedAdSlot(adContent);

        adItem.appendChild(adLabel);
        adItem.appendChild(adContent);
        adContainer.appendChild(adItem);
        sidebar.appendChild(adContainer);
        if (i === desktopSidebarPlacement.stickySlotIndex) {
          configureStickySidebarRail(adContainer, adItem, sidebar);
        }
      }

      try {
        if (desktopSidebarPlacement.stickySlotIndex === null) {
          const mainColumn = document.querySelector('.main-column');
          const sidebarContainers = sidebar.querySelectorAll('.sidebar-ad-container');
          if (mainColumn && sidebarContainers.length >= 4) {
            const containerHeight = mainColumn.offsetHeight;
            const target = sidebarContainers[3];
            target.style.height = `calc(${containerHeight}px - 4800px)`;
          }
        }
      } catch (err) {
        logWarn('Failed to adjust sidebar ad container height', err);
      }
    }

    function initializeAds() {
      const adRuntime = createPageAdRuntime({
        brand,
        pageType: pageType || 'article',
        device: isTablet ? 'tablet' : 'desktop',
        pageKey,
        auctionStrategy,
        diagnosticsEnabled: adDiagnosticsEnabled,
        pbjs,
        googletag,
        timeoutMs: PREBID_TIMEOUT
      });
      const coordinator = adRuntime.coordinator;
      const contentAdRequestThreshold = resolveCoordinatedRequestThreshold({
        coordinated: adRuntime.coordinated,
        legacyThreshold: CONTENT_AD_PREFETCH_THRESHOLD,
        device: isTablet ? 'tablet' : device
      });
      const sidebarAdRequestThreshold = resolveCoordinatedRequestThreshold({
        coordinated: adRuntime.coordinated,
        legacyThreshold: SIDEBAR_AD_PREFETCH_THRESHOLD,
        device: isTablet ? 'tablet' : device
      });
      desktopAnchorCoordinator = coordinator;
      adRuntime.addCleanup(() => {
        if (stickySidebarResizeObserver) {
          stickySidebarResizeObserver.disconnect();
          stickySidebarResizeObserver = null;
        }
        if (desktopAnchorView) {
          desktopAnchorView.destroy();
          desktopAnchorView = null;
        }
        desktopAnchorGptSlot = null;
        desktopAnchorAutoRefreshController = null;
        desktopAnchorCoordinator = null;
      });
      // Generate ad units using factory with article-specific sizes
      const adUnits = adRuntime.coordinated
        ? []
        : [makeTopAdUnit(config, { sizes: topAdSizes })];

      // Skin and sidebar ads are desktop-only (tablet has no sidebar)
      if (!adRuntime.coordinated && skinEnabled) {
        adUnits.push(makeSkinAdUnit(config));
      }

      // Generate content ad units
      if (!adRuntime.coordinated) {
        for (let i = 1; i <= 7; i += 1) {
          adUnits.push(makeDtContentAdUnit(i, config, { sizes: dtContentSizes }));
        }
      }

      // Sidebar ads are desktop-only
      if (!adRuntime.coordinated && !isTablet) {
        for (let i = 1; i <= activeSidebarSlotCount; i += 1) {
          adUnits.push(makeSidebarAdUnit(i, config, { sizes: sidebarSizes }));
        }
      }

      pbjs.que.push(function () {
        configurePrebid(pbjs, userIds);
        if (adRuntime.coordinated) {
          return;
        }
        pbjs.addAdUnits(adUnits);
        pbjs.requestBids({
          timeout: PREBID_TIMEOUT,
          bidsBackHandler: function () {
            initAdserver();
          }
        });
      });

      function initAdserver() {
        if (!pbjs || typeof pbjs.getBidResponses !== 'function' || pbjs.initAdserverSet) {
          return;
        }
        pbjs.initAdserverSet = true;
      }

      if (!adRuntime.coordinated) {
        setTimeout(initAdserver, FAILSAFE_TIMEOUT);
      }

      googletag.cmd.push(function () {
        const REFRESH_KEY = 'refresh';
        const REFRESH_VALUE = 'true';
        const staticSlots = [];
        adRuntime.observeGpt(googletag.pubads());

        if (!adRuntime.coordinated) {
          enableLazyLoad(googletag, googletag.pubads(), {
            fetchMarginPercent: 120,
            renderMarginPercent: 20,
            mobileScaling: 2.0
          });
        }

        googletag
          .pubads()
          .addEventListener('slotRenderEnded', function (event) {
            const slot = event.slot;
            const slotId = slot.getSlotElementId && slot.getSlotElementId();
            const div = document.getElementById(slotId);
            const size = event.size;
            const shouldCollapseManagedSlot =
              div &&
              isManagedSlotId(slotId) &&
              !(typeof window !== 'undefined' && window._millaShowAdPlaceholders === true) &&
              (event.isEmpty || isCollapsibleTinyCreative(slotId, size));

            if (shouldCollapseManagedSlot) {
              collapseManagedAdSlot(div);
            } else if (div && isManagedSlotId(slotId)) {
              restoreManagedAdSlot(div);
            }

            if (!shouldCollapseManagedSlot && size && size.length === 2 && div) {
              if (size && !(size[0] === 0 && size[1] === 0)) {
                if (
                  size[0] === 1 ||
                  (size[0] === 2000 && (size[1] === 1 || size[1] === 250))
                ) {
                  div.style.height = 'auto';
                } else if (size[0] === 2000 && size[1] === 1200) {
                  div.style.height = '0px';
                } else {
                  div.style.height = `${size[1]}px`;
                  div.style.width = `${size[0]}px`;
                }
              } else if (size[0] === 0 && size[1] === 0) {
                div.style.width = 'auto';
                div.style.height = 'auto';
              }
              div.style.margin = 'auto';
            }

            if (
              !shouldCollapseManagedSlot &&
              !event.isEmpty &&
              div &&
              isContentSlotId(slotId)
            ) {
              applyContentAdSticky(div, size);
            }

            // Reset container size if slot is empty (GPT returned no ad)
            // Keep min-height to avoid CLS when empty creatives collapse.
            if (!shouldCollapseManagedSlot && event.isEmpty && div) {
              div.style.height = 'auto';
            }

            try {
              emitAdEvent(emitter, brand, 'slotRender', {
                slotId: slot.getSlotElementId(),
                adUnitPath: slot.getAdUnitPath && slot.getAdUnitPath(),
                size
              });
            } catch (e) {
              logWarn('Failed to emit slotRender event', e);
            }
          });

        applyGamTargeting(googletag, storiesPageTargeting);

        // PPID = the derived, consent-gated ads id only (2026-09-09); no sharedId fallback.
        if (window.ppid) {
          googletag.pubads().setPublisherProvidedId(window.ppid);
        }

        configurePageSettings(googletag, googletag.pubads(), {
          disableInitialLoad: true,
          singleRequest: true,
          collapseDiv:
            typeof window !== 'undefined' && window._millaShowAdPlaceholders === true
              ? null
              : 'ON_NO_FILL'
        });
        googletag.enableServices();

        desktopAnchorAutoRefreshController = setupAutoRefresh(googletag, pbjs, {
          refreshKey: REFRESH_KEY,
          refreshValue: REFRESH_VALUE,
          prebidTimeout: PREBID_TIMEOUT,
          emptyInitialDelayMs: adRuntime.coordinated ? 15000 : undefined,
          emptyDelayMs: adRuntime.coordinated ? 30000 : 10000,
          filledDelayMs: 30000,
          viewabilityThreshold: 0.5,
          viewabilityTimeMs: 1000,
          maxRefreshes: 10,
          maxEmptyRefreshes: adRuntime.coordinated ? 2 : undefined,
          maxFilledRefreshes: adRuntime.coordinated ? 10 : undefined,
          preloadMarginPx: adRuntime.coordinated ? 0 : undefined,
          coordinator,
          shouldApplyPrebidSize: (slotId) => slotId !== 'top-ad'
        });

        // Skin ad is desktop-only. Its slot sits in the header after the top-ad
        // wrapper, so GPT's 2000x1200 container must stay out of the page flow
        // while the request is in flight.
        if (skinEnabled) {
          const skinAd = document.getElementById('skin-ad');
          prepareManagedAdSlot(skinAd);
          prepareSkinAdSlot(skinAd);
          const skinSlot = googletag
            .defineSlot(getSlotPath(config, 'desktop', 'skin'), skinSize, 'skin-ad')
            .addService(googletag.pubads());
          googletag.display('skin-ad');
          staticSlots.push(skinSlot);
        }

        if (_sponsored !== 'yes') {
          const brandSnippetId = `brandsnippet-${brand}`;
          const brandSnippetSlot = applySlotTargeting(
            googletag.defineSlot(
              getSlotPath(config, 'desktop', 'brandSnippet'),
              ['fluid'],
              brandSnippetId
            ),
            { lazyload: 'lazyload' }
          ).addService(googletag.pubads());
          googletag.display(brandSnippetId);
          staticSlots.push(brandSnippetSlot);
        }

        if (staticSlots.length > 0) {
          googletag.pubads().refresh(staticSlots);
          staticSlots.forEach((slot) => {
            const slotId = slot && slot.getSlotElementId && slot.getSlotElementId();
            scheduleManagedSlotFailsafe(slotId);
          });
        }

        prepareManagedAdSlot(document.getElementById('top-ad'));
        const topAdSlot = applySlotTargeting(
          googletag.defineSlot(getSlotPath(config, 'desktop', 'topAd'), topAdSizes, 'top-ad'),
          {
            lazyload: 'lazyload',
            [REFRESH_KEY]: REFRESH_VALUE
          }
        ).addService(googletag.pubads());

        if (coordinator) {
          coordinator.registerSlot({
            slotId: 'top-ad',
            gptSlot: topAdSlot,
            createAdUnit: () => makeTopAdUnit(config, { sizes: topAdSizes }),
            placement: { slotFamily: 'top', slotIndex: 1 },
            applyPrebidSize: false
          });
          coordinator.requestInitial('top-ad').then((result) => {
            if (result.status === 'no_bid') {
              logDebug('slot:noPrebidBid', { slotId: 'top-ad' });
              releaseManagedAdReservation(document.getElementById('top-ad'));
            }
            scheduleManagedSlotFailsafe('top-ad');
          });
        } else {
          pbjs.requestBids({
            timeout: PREBID_TIMEOUT,
            adUnitCodes: ['top-ad'],
            bidsBackHandler: function () {
              googletag.cmd.push(function () {
                if (!hasPrebidWinningBid('top-ad')) {
                  logDebug('slot:noPrebidBid', { slotId: 'top-ad' });
                  releaseManagedAdReservation(document.getElementById('top-ad'));
                }
                applyPrebidTargetingToSlots(pbjs, topAdSlot);
                scheduleManagedSlotFailsafe('top-ad');
                googletag.pubads().refresh([topAdSlot]);
              });
            }
          });
        }

        const anchorView = ensureDesktopAnchorView();
        if (anchorView) {
          prepareManagedAdSlot(anchorView.adContainer);
          desktopAnchorGptSlot = applySlotTargeting(
            googletag.defineSlot(
              getSlotPath(config, 'desktop', 'topAd'),
              desktopAnchorSizes,
              desktopAnchorSlotId
            ),
            {
              lazyload: 'lazyload',
              [REFRESH_KEY]: REFRESH_VALUE,
              placement: 'desktop_anchor',
              slot_index: '1'
            }
          ).addService(googletag.pubads());
          googletag.display(desktopAnchorSlotId);

          const createDesktopAnchorUnit = () => makeTopAdUnit(config, {
            code: desktopAnchorSlotId,
            index: 2,
            sizes: desktopAnchorSizes
          });

          if (coordinator) {
            coordinator.registerSlot({
              slotId: desktopAnchorSlotId,
              gptSlot: desktopAnchorGptSlot,
              createAdUnit: createDesktopAnchorUnit,
              placement: { slotFamily: 'desktop_anchor', slotIndex: 1 }
            });
            coordinator.requestInitial(desktopAnchorSlotId).then((result) => {
              if (result.status === 'no_bid') {
                releaseManagedAdReservation(document.getElementById(desktopAnchorSlotId));
              }
              scheduleManagedSlotFailsafe(desktopAnchorSlotId);
            });
          } else {
            const existingAnchorUnits = typeof pbjs.getAdUnits === 'function'
              ? pbjs.getAdUnits()
              : pbjs.adUnits;
            if (
              typeof pbjs.addAdUnits === 'function'
              && !(
                Array.isArray(existingAnchorUnits)
                && existingAnchorUnits.some((unit) => unit && unit.code === desktopAnchorSlotId)
              )
            ) {
              pbjs.addAdUnits([createDesktopAnchorUnit()]);
            }
            pbjs.requestBids({
              timeout: PREBID_TIMEOUT,
              adUnitCodes: [desktopAnchorSlotId],
              bidsBackHandler: function () {
                googletag.cmd.push(function () {
                  if (!hasPrebidWinningBid(desktopAnchorSlotId)) {
                    releaseManagedAdReservation(document.getElementById(desktopAnchorSlotId));
                  } else {
                    applySlotSizeFromPrebid(desktopAnchorSlotId, pbjs);
                  }
                  applyPrebidTargetingToSlots(pbjs, desktopAnchorGptSlot);
                  scheduleManagedSlotFailsafe(desktopAnchorSlotId);
                  googletag.pubads().refresh([desktopAnchorGptSlot]);
                });
              }
            });
          }
        }

        onElementInViewport({
          selector: '.content-ad',
          onIntersect: function (entry) {
            const slotId = entry && entry.target && entry.target.id;
            if (!slotId) return;
            logDebug('slot:define', {
              slotId,
              adUnit: getSlotPath(config, 'desktop', 'content')
            });
            ensureAdSlotMinHeight(entry.target, CONTENT_SLOT_MIN_HEIGHT);
            prepareManagedAdSlot(entry.target);
            // collapseManagedAdSlot releases an empty slot's space, off screen.
            const adSlot = disableSlotCollapse(applySlotTargeting(
              googletag.defineSlot(
                getSlotPath(config, 'desktop', 'content'),
                dtContentSizes,
                slotId
              ),
              {
                lazyload: 'lazyload',
                [REFRESH_KEY]: REFRESH_VALUE
              }
            )).addService(googletag.pubads());
            logDebug('slot:display', { slotId });
            googletag.display(slotId);

            logDebug('slot:requestBids', { slotId, timeout: PREBID_TIMEOUT });
            if (coordinator) {
              const slotIndex = Number((slotId.match(/-(\d+)$/) || [])[1]);
              coordinator.registerSlot({
                slotId,
                gptSlot: adSlot,
                createAdUnit: () => makeDtContentAdUnit(slotIndex, config, { sizes: dtContentSizes }),
                placement: { slotFamily: 'content', slotIndex }
              });
              coordinator.requestInitial(slotId).then((result) => {
                if (result.status === 'no_bid') {
                  logDebug('slot:noPrebidBid', { slotId });
                  releaseManagedAdReservation(document.getElementById(slotId));
                }
                scheduleManagedSlotFailsafe(slotId);
              });
            } else {
              ensurePrebidAdUnitForCode(pbjs, slotId, config, 'desktop');
              pbjs.requestBids({
                timeout: PREBID_TIMEOUT,
                adUnitCodes: [slotId],
                bidsBackHandler: function () {
                  googletag.cmd.push(function () {
                    logDebug('slot:bidsBack', { slotId });
                    if (!hasPrebidWinningBid(slotId)) {
                      logDebug('slot:noPrebidBid', { slotId });
                      releaseManagedAdReservation(document.getElementById(slotId));
                    } else {
                      applySlotSizeFromPrebid(slotId, pbjs);
                    }
                    applyPrebidTargetingToSlots(pbjs, adSlot);
                    logDebug('slot:refresh', { slotId, reason: 'initial' });
                    scheduleManagedSlotFailsafe(slotId);
                    googletag.pubads().refresh([adSlot]);
                  });
                }
              });
            }
          },
          threshold: contentAdRequestThreshold,
          oneTime: true
        });

        // Sidebar viewport observer is desktop-only
        if (!isTablet) {
          onElementInViewport({
            selector: '.sidebar-content',
            onIntersect: function (entry) {
              const slotId = entry && entry.target && entry.target.id;
              if (!slotId) return;
              logDebug('slot:define', {
                slotId,
                adUnit: getSlotPath(config, 'desktop', 'sidebar')
              });
              ensureAdSlotMinHeight(entry.target, SIDEBAR_SLOT_MIN_HEIGHT);
              prepareManagedAdSlot(entry.target);
              const adSlot = applySlotTargeting(
                googletag.defineSlot(
                  getSlotPath(config, 'desktop', 'sidebar'),
                  sidebarSizes,
                  slotId
                ),
                {
                  lazyload: 'lazyload',
                  [REFRESH_KEY]: REFRESH_VALUE
                }
              ).addService(googletag.pubads());
              logDebug('slot:display', { slotId });
              googletag.display(slotId);

              logDebug('slot:requestBids', { slotId, timeout: PREBID_TIMEOUT });
              if (coordinator) {
                const slotIndex = Number((slotId.match(/-(\d+)$/) || [])[1]);
                coordinator.registerSlot({
                  slotId,
                  gptSlot: adSlot,
                  createAdUnit: () => makeSidebarAdUnit(slotIndex, config, { sizes: sidebarSizes }),
                  placement: { slotFamily: 'sidebar', slotIndex }
                });
                coordinator.requestInitial(slotId).then((result) => {
                  if (result.status === 'no_bid') {
                    logDebug('slot:noPrebidBid', { slotId });
                    releaseManagedAdReservation(document.getElementById(slotId));
                  }
                  scheduleManagedSlotFailsafe(slotId);
                });
              } else {
                ensurePrebidAdUnitForCode(pbjs, slotId, config, 'desktop');
                pbjs.requestBids({
                  timeout: PREBID_TIMEOUT,
                  adUnitCodes: [slotId],
                  bidsBackHandler: function () {
                    googletag.cmd.push(function () {
                      logDebug('slot:bidsBack', { slotId });
                      if (!hasPrebidWinningBid(slotId)) {
                        logDebug('slot:noPrebidBid', { slotId });
                        releaseManagedAdReservation(document.getElementById(slotId));
                      } else {
                        applySlotSizeFromPrebid(slotId, pbjs);
                      }
                      applyPrebidTargetingToSlots(pbjs, adSlot);
                      logDebug('slot:refresh', { slotId, reason: 'initial' });
                      scheduleManagedSlotFailsafe(slotId);
                      googletag.pubads().refresh([adSlot]);
                    });
                  }
                });
              }
            },
            threshold: sidebarAdRequestThreshold,
            oneTime: true
          });
        }

        if (!storiesPlayerConfig.showAware) storiesPlayer(ctx);

      });

      // Tablet sticky bottom ad (769-1024px) - uses shared helper
      setupTabletStickyAd(googletag, getSlotPath(config, 'desktop', 'topAd'));
    }

    if (!(_noads === 'yes' || _urlonly === 'yes' || pro === 'active')) {
      loadScript(gptUrl)
        .then(() => {
          return loadScriptWithin(prebidUrl, PREBID_LOAD_FAILSAFE_MS)
            .then(() => {
              initializeAds();
              if (typeof window !== 'undefined' && window._millaShowAdPlaceholders === true) {
                setTimeout(renderDebugPlaceholders, 3000);
              }
            })
            .catch((err) => {
              logWarn('Prebid failed to load; continuing with GPT-only ads', err);
              ensurePrebidFallback(pbjs);
              initializeAds();
              if (typeof window !== 'undefined' && window._millaShowAdPlaceholders === true) {
                setTimeout(renderDebugPlaceholders, 3000);
              }
            });
        })
        .catch((err) => {
          logError('Failed to load GPT script', err);
          collapseAllPendingManagedSlots();
          emitAdEvent(emitter, brand, 'error', err);
        });
    }

    if (typeof document !== 'undefined') {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', injectTopAndSkinAdContainers);
      } else {
        injectTopAndSkinAdContainers();
      }
    }

    // CDM container for articles — fires immediately on page load, no viewability wait
    if (
      typeof document !== 'undefined' &&
      typeof _googCsa === 'function' &&
      !(_noads === 'yes' || _urlonly === 'yes' || pro === 'active' || _sponsored === 'yes' || staticPage || walled)
    ) {
      const initArticleCdm = () => {
        const articleCsaConfig = config.articleCsa || null;
        if (!articleCsaConfig) return;
        const cdmContainerId = articleCsaConfig.container || 'cdm-container-article';
        let cdmContainer = document.getElementById(cdmContainerId);
        if (!cdmContainer) {
          cdmContainer = document.createElement('div');
          cdmContainer.id = cdmContainerId;
          cdmContainer.className = 'article-cdm-container';
          cdmContainer.style.margin = '24px 0';
          cdmContainer.style.width = '100%';
          const articleBody = document.querySelector('.body-description')
            || document.querySelector('.article-body');
          if (articleBody && articleBody.parentElement) {
            articleBody.parentElement.insertBefore(cdmContainer, articleBody.nextSibling);
          }
        }
        if (cdmContainer) {
          try {
            _googCsa('cdm', buildCdmPageOptions(articleCsaConfig), { container: cdmContainerId });
          } catch (err) {
            logWarn('Failed to initialize article CDM block (desktop)', err);
          }
        }
      };
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', initArticleCdm);
      } else {
        initArticleCdm();
      }
    }

    // Sidebar injection is desktop-only (tablet has no sidebar)
    if (!isTablet && !(_noads === 'yes' || _urlonly === 'yes' || pro === 'active')) {
      if (typeof document !== 'undefined') {
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', injectSidebarNewsletterAndAds);
        } else {
          injectSidebarNewsletterAndAds();
        }
      }
    }
  // Inject an inline newsletter box in the article body for logged-out users
  if (
    typeof document !== 'undefined' &&
    !(_noads === 'yes' || _urlonly === 'yes' || _sponsored === 'yes' || pro === 'active') &&
    !(ctx && ctx.isLoggedIn === true) &&
    !walled
  ) {
    const injectInlineNewsletter = () => {
      try {
        const container =
          document.querySelector('.body-description') ||
          document.querySelector('article') ||
          document.querySelector('.article-body');
        if (!container) {
          return;
        }
        const paragraphs = Array.from(container.querySelectorAll('p')).filter((p) => {
          if (!p || isBlockedEmbedParagraph(p)) return false;
          const text = (p.textContent || '').trim();
          return text.length > 0;
        });

        if (paragraphs.length === 0) {
          return;
        }

        const insertAfter = (targetNode, moduleEl) => {
          if (!targetNode || !targetNode.parentNode || !moduleEl) return;
          targetNode.parentNode.insertBefore(moduleEl, targetNode.nextSibling);
        };
        // Mid-article placement: after ~4th paragraph (best visibility without being too early)
        if (!container.querySelector('.inline-newsletter-box') && paragraphs.length >= 4) {
          const target = paragraphs[3];
          const moduleEl = createNewsletterIframeModule('article_mid');
          if (moduleEl) {
            moduleEl.classList.add('inline-newsletter-box');
            moduleEl.classList.add('milla-newsletter-embed');
            insertAfter(target, moduleEl);
          }
        }

        // Near-end placement: engaged readers are high-intent.
        // Limit to longer articles to avoid overloading short reads.
        // The article-end slot is exclusive: yield to a survey/prediction/
        // contest embed when one renders there.
        waitForArticleEndSlot().then((occupied) => {
          if (occupied) return;
          try {
            if (!container.querySelector('.inline-newsletter-box-end') && paragraphs.length >= 7) {
              const target = paragraphs[paragraphs.length - 1];
              const moduleEl = createNewsletterIframeModule('article_end');
              if (moduleEl) {
                moduleEl.classList.add('inline-newsletter-box-end');
                moduleEl.classList.add('milla-newsletter-embed');
                insertAfter(target, moduleEl);
              }
            }
          } catch (err) {
            logWarn('Failed to inject inline newsletter box', err);
          }
        });
      } catch (err) {
        logWarn('Failed to inject inline newsletter box', err);
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', injectInlineNewsletter);
    } else {
      injectInlineNewsletter();
    }
  }

  // Inject contest/insights widget at the bottom of the article body when active
  if (typeof document !== 'undefined') {
    const injectInsightsWidget = () => {
      try {
        const articleId = resolveInsightsArticleId();
        if (!articleId || staticPage || walled) {
          settleArticleEndSlot(false);
          return;
        }

        ensureInsightsCompletionListener();
        // A brand lift study reader names its study and group
        // (lib/insights/study.js). While this page's ads can still settle the
        // group, selection waits up to STUDY_GROUP_WAIT_MS and the
        // article-end decision is held for as long.
        const studyGroupReady = shouldSuppressInsightSurveyEmbed(ctx)
          ? Promise.resolve(null)
          : waitForStudyGroup(config.brand, STUDY_GROUP_WAIT_MS, { onWait: extendArticleEndSlotDecision });
        studyGroupReady
          .then((studyGroup) => fetchActiveEmbedItem(articleId, {
            brand: config.brand,
            language,
            tags: readArticleTags(ctx),
            sponsored: isSponsoredContent(ctx),
            excludeSurveyIds: listCompletedInsightsSurveyIds(config.brand),
            study: studyGroup ? studyGroup.studyKey : undefined,
            group: studyGroup ? studyGroup.group : undefined
          }).then((item) => ({ item, studyGroup })))
          .then(async ({ item, studyGroup }) => {
            if (!item || !item.payload) return false;
            const payload = item.payload;
            if (item.type !== 'contest' && shouldSuppressInsightSurveyEmbed(ctx)) return false;
            if (item.type === 'prediction' && isPredictionFrequencyCapped(config.brand, payload.id || payload._id)) return false;
            if (item.type === 'survey' && isInsightsFrequencyCapped(config.brand, payload.id || payload._id)) return false;

            const container =
              document.querySelector('.body-description') ||
              document.querySelector('article') ||
              document.querySelector('.article-body');
            if (!container || container.querySelector('.milla-contest-embed, .milla-insights-embed')) {
              return false;
            }

            const paragraphs = Array.from(container.querySelectorAll('p')).filter((p) => {
              if (!p || isBlockedEmbedParagraph(p)) return false;
              const text = (p.textContent || '').trim();
              return text.length > 0;
            });

            const insertAfter = (targetNode, moduleEl) => {
              if (!moduleEl) return;
              if (targetNode && targetNode.parentNode) {
                targetNode.parentNode.insertBefore(moduleEl, targetNode.nextSibling);
                return;
              }
              if (container && typeof container.appendChild === 'function') {
                container.appendChild(moduleEl);
              }
            };

            const anchor =
              container.querySelector('.inline-newsletter-box-end') ||
              (paragraphs.length ? paragraphs[paragraphs.length - 1] : null) ||
              container.lastElementChild;

            const moduleEl = item.type === 'contest'
              ? createContestIframeModule({
                  contestId: payload.id || payload._id,
                  articleId,
                  location: 'article_end'
                })
              : (item.type === 'prediction'
                ? await createPredictionIframeModule({
                    predictionId: payload.id || payload._id,
                    publicSlug: payload.publicSlug,
                    articleId,
                    brand: config.brand,
                    language
                  })
                : await createInsightsIframeModule({
                  surveyId: payload.id || payload._id,
                  surveyType: payload.type,
                  articleId,
                  location: 'article_end',
                  brand: config.brand,
                  language,
                  insights,
                  // The study survey's impression carries the exposure signal.
                  studyKey: studyGroup && payload.studyKey === studyGroup.studyKey
                    ? studyGroup.studyKey
                    : undefined
                }));
            if (moduleEl) {
              moduleEl.classList.add(item.type === 'contest' ? 'milla-contest-embed' : 'milla-insights-embed');
              insertAfter(anchor, moduleEl);
              return true;
            }
            return false;
          })
          .then((placed) => {
            settleArticleEndSlot(placed === true);
          })
          .catch((err) => {
            settleArticleEndSlot(false);
            logWarn('Failed to fetch active embed item', err);
          });
      } catch (err) {
        settleArticleEndSlot(false);
        logWarn('Failed to inject insights widget', err);
      }
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', injectInsightsWidget);
    } else {
      injectInsightsWidget();
    }
  }

  const topadwrapper = document.querySelector('.top-ad-wrapper');
  const brandsnippetarticle = document.querySelector('.brandsnippet-article');

  if (shouldHideTopAdWrapper(_noads, pro)) {
    hideTopAdWrapper();
  }
  if (_noads === 'yes' && brandsnippetarticle) {
    brandsnippetarticle.style.display = 'none';
  }

  if (!(_noads === 'yes' || _urlonly === 'yes' || pro === 'active') && topadwrapper) {
    topadwrapper.style.display = 'block';
  }

  if (!(_noads === 'yes' || _urlonly === 'yes' || _sponsored === 'yes' || pro === 'active')) {
    function insertAd(element, ad, slotIds) {
      const parentElement = element.parentElement;
      if (!parentElement) return;
      const directParagraph = parentElement.classList.contains('body-description');
      const anchor = directParagraph ? element
        : element.closest('.rebellt-item') || element.closest('.rm-shortcode');
      if (!anchor) return;

      // Check the actual insertion boundary. CMS output can leave empty <p>
      // nodes between the intro and list, and long paragraphs cross several
      // thresholds; neither should permit two breaks at the same boundary.
      const siblingKey = directParagraph ? 'nextElementSibling' : 'previousElementSibling';
      let adjacent = anchor[siblingKey];
      while (adjacent && adjacent.tagName === 'P' && !adjacent.textContent.trim()
        && !adjacent.querySelector('img, iframe, video, audio, svg, canvas, object, embed')) {
        adjacent = adjacent[siblingKey];
      }
      if (adjacent && adjacent.matches('.separator, .stories-player-container, .ad-wrapper, .related-post')) return;

      anchor.insertAdjacentHTML(directParagraph ? 'afterend' : 'beforebegin', ad);

      const ids = Array.isArray(slotIds) ? slotIds : (slotIds ? [slotIds] : []);
      ids.forEach((slotId) => {
        if (!slotId) return;
        const slot = document.getElementById(slotId);
        ensureAdSlotMinHeight(slot, CONTENT_SLOT_MIN_HEIGHT);
        prepareManagedAdSlot(slot);
      });
    }

    // A walled view requests nothing in the body (plans/dynamic-login-wall.md).
    const pElements = walled ? [] : document.querySelectorAll('.body-description p');
    // innerText forces layout. Read every paragraph before inserting any ad,
    // so the whole pass costs one layout instead of one per inserted ad.
    const paragraphWordCounts = Array.from(pElements, (element) => element.innerText.split(' ').length);
    let wordCount = 0;

    pElements.forEach((element, index) => {
      for (let word = 0; word < paragraphWordCounts[index]; word += 1) {
        wordCount += 1;
        if (wordCount % 120 === 0 && wordCount <= 1700) {
          const adNumber = wordCount / 120;
          if (adNumber === 1) {
            const relatedDiv = buildRelatedSearchContainerHtml(
              isTablet ? 0 : RELATED_SEARCH_MIN_HEIGHT
            );
            // On tablet, inject stories player container inline (no sidebar)
            const storiesContainerHTML = isTablet
              ? `<div class="stories-player-container" style="padding:15px;background:#F9F9F9;box-sizing:border-box;"><a href="${proLinkUrl}" class="requires-pro milla-pro-link" target="_blank" rel="noopener noreferrer">${proLinkText}</a></div>`
              : '';

            const ad1 = `
    <div class="separator"><div class="separator-top"></div></div>
    <div class="ad-wrapper">
      <div class="ad-flex">
        <a href="${proLinkUrl}" class="requires-pro milla-pro-link" target="_blank" rel="noopener noreferrer">${proLinkText}</a>
        <div style="display:flex;width:100%;">
          <div id="dt-content-ad-1" class="content-ad"></div>
        </div>
      </div>
    </div>
    ${storiesContainerHTML}
    ${relatedDiv}
    <div class="separator"><div class="separator-bottom"></div></div>
  `;

            insertAd(element, ad1, 'dt-content-ad-1');
            window.adData.placedAdCount += 2;
          } else if (adNumber === 2) {
            const ad2 = `
    <div class="separator"><div class="separator-top"></div></div>
    <div class="ad-wrapper">
      <div class="ad-flex">
        <a href="${proLinkUrl}" class="requires-pro milla-pro-link" target="_blank" rel="noopener noreferrer">${proLinkText}</a>
        <div id="dt-content-ad-${window.adData.placedAdCount}" class="content-ad"></div>
      </div>
    </div>
    <div class="separator"><div class="separator-bottom"></div></div>
  `;
            insertAd(element, ad2, `dt-content-ad-${window.adData.placedAdCount}`);
            window.adData.placedAdCount += 1;
          } else {
            const ad = `<div class="separator"><div class="separator-top"></div></div><div class="related-post"></div><div class="ad-wrapper"><div class="ad-flex"><a href="${proLinkUrl}" class="requires-pro milla-pro-link" target="_blank" rel="noopener noreferrer">${proLinkText}</a><div id="dt-content-ad-${window.adData.placedAdCount}" class="content-ad"></div></div></div><div class="separator"><div class="separator-bottom"></div></div>`;
            insertAd(element, ad, `dt-content-ad-${window.adData.placedAdCount}`);
            window.adData.placedAdCount += 1;
          }
        }
      }
    });

    const articleInfiniteAdOptions = {
      slotPrefix: 'dt-content-ad',
      proLinkUrl,
      proLinkText,
      minHeight: CONTENT_SLOT_MIN_HEIGHT,
      minDistancePx: INFINITE_SCROLL_AD_MIN_DISTANCE_PX,
      itemInterval: INFINITE_SCROLL_AD_ITEM_INTERVAL,
      prepareSlot: prepareManagedAdSlot
    };
    window.insertNewAd = createArticleInfiniteAdInserter(articleInfiniteAdOptions);
    setupArticleInfiniteScrollAdsObserver(articleInfiniteAdOptions);

    // On tablet, ensure a stories container exists inline if the word-count loop
    // didn't place one (e.g. short articles). Desktop uses the sidebar instead.
    if (isTablet && !walled) {
      (function ensureStoriesContainerFallback() {
        if (typeof document === 'undefined') return;

        const insertIfReady = () => {
          const container =
            document.querySelector('.body-description')
            || document.querySelector('article')
            || document.querySelector('.article-body');
          if (!container) return false;
          if (container.querySelector('.stories-player-container')) return true;

          const paragraphs = Array.from(container.querySelectorAll('p')).filter((p) => {
            if (!p || isBlockedEmbedParagraph(p)) return false;
            const text = (p.textContent || '').trim();
            return text.length > 0;
          });

          let target = paragraphs[0] || container.firstElementChild || container;
          if (paragraphs.length > 0) {
            let wordsSeen = 0;
            for (let i = 0; i < paragraphs.length; i += 1) {
              const text = (paragraphs[i].innerText || paragraphs[i].textContent || '').trim();
              wordsSeen += text ? text.split(/\s+/).filter(Boolean).length : 0;
              target = paragraphs[i];
              if (wordsSeen >= 100) break;
            }
          }

          const storiesHTML = '<div class="stories-player-container stories-player-container--fallback" style="padding:15px;background:#F9F9F9;box-sizing:border-box;">'
            + '<a href="' + proLinkUrl + '" class="requires-pro milla-pro-link" target="_blank" rel="noopener noreferrer">' + proLinkText + '</a>'
            + '</div>';

          const parentElem = target.parentElement;
          let inserted = false;
          if (parentElem && parentElem.classList.contains('body-description')) {
            target.insertAdjacentHTML('afterend', storiesHTML);
            inserted = true;
          } else if (parentElem && parentElem.classList.contains('rebellt-item')) {
            parentElem.insertAdjacentHTML('beforeBegin', storiesHTML);
            inserted = true;
          } else if (parentElem && parentElem.closest('.rebellt-item')) {
            parentElem.closest('.rebellt-item').insertAdjacentHTML('beforeBegin', storiesHTML);
            inserted = true;
          } else if (parentElem && parentElem.closest('.rm-shortcode')) {
            parentElem.closest('.rm-shortcode').insertAdjacentHTML('beforeBegin', storiesHTML);
            inserted = true;
          }

          if (!inserted) {
            if (target && target !== container && typeof target.insertAdjacentHTML === 'function') {
              target.insertAdjacentHTML('afterend', storiesHTML);
            } else if (typeof container.insertAdjacentHTML === 'function') {
              container.insertAdjacentHTML('afterbegin', storiesHTML);
            }
          }

          return true;
        };

        if (insertIfReady()) return;

        if (typeof MutationObserver === 'undefined' || window.__millaStoriesFallbackObserverActive) return;

        const root = document.documentElement || document.body;
        if (!root) return;

        const observer = new MutationObserver(() => {
          if (insertIfReady()) {
            observer.disconnect();
            window.__millaStoriesFallbackObserverActive = false;
          }
        });

        observer.observe(root, { childList: true, subtree: true });
        window.__millaStoriesFallbackObserverActive = true;

        setTimeout(() => {
          if (window.__millaStoriesFallbackObserverActive) {
            observer.disconnect();
            window.__millaStoriesFallbackObserverActive = false;
          }
        }, 15000);
      })();
    }

    if (!walled) {
      setupMemberArticleAds(ctx, {
        device,
        slotPrefix: 'dt-content-ad',
        minHeight: CONTENT_SLOT_MIN_HEIGHT,
        prepareSlot: prepareManagedAdSlot
      });

      const pageOptions = {
        pubId: 'partner-pub-7289986524496939',
        styleId: '7477868506',
        relatedSearchTargeting: 'content',
        resultsPageBaseUrl,
        resultsPageQueryParam: 'q'
      };

      const rsblock1 = {
        container: 'relatedsearches1',
        relatedSearches: 5
      };

      if (typeof _googCsa === 'function') {
        _googCsa('relatedsearch', pageOptions, rsblock1);
      }
    }

  }

    emitAdEvent(emitter, brand, 'ready');
  };
}

module.exports = {
  createArticleDesktopAds
};
